import { DocumentData, SupportingDocumentMetadata } from "types";
import { defaultSupportingMetadata } from "components/Documents/supportingMetadata";
import { useMutation, useQueryClient } from "react-query";
import axios, { AxiosInstance } from "axios";
import { useClient } from "utils/client";
import {
  encodeObjectPath,
  sanitizeSupportingFilename,
} from "components/Documents/supportingDocumentFiles";
import { SpecialUploadType } from "components/Documents/types";

export interface SupportingUpload {
  file: File;
  name: string;
  uploaded?: boolean;
  metadata?: SupportingDocumentMetadata;
}
export const prepareSupportingUpload = (file: File): SupportingUpload => {
  if (!file.size) throw new Error("supportingDocuments.invalidFile");
  const id = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return {
    file,
    metadata: defaultSupportingMetadata(file.name),
    name: `/supporting-documents/${id}/${sanitizeSupportingFilename(file.name)}`,
  };
};
export const uploadSupportingDocument = async (
  client: AxiosInstance,
  projectId: string,
  upload: SupportingUpload,
) => {
  if (!upload.uploaded) {
    const { data: url } = await client.post(
      `/documents/signing/${encodeURIComponent(projectId)}/${encodeObjectPath(upload.name.slice(1))}`,
    );
    await axios.put(url, upload.file, {
      headers: {
        "Content-Type": upload.file.type || "application/octet-stream",
      },
    });
    upload.uploaded = true;
  }
  await client.post(`/projects/${encodeURIComponent(projectId)}/documents`, {
    type: SpecialUploadType.supportingDocument,
    name: upload.name,
    metadata: upload.metadata,
    file_size: upload.file.size,
    content_type: upload.file.type || "application/octet-stream",
  });
};
export const useSupportingDocuments = (projectId: string) => {
  const client = useClient();
  const queries = useQueryClient();
  const refresh = () =>
    Promise.all([
      queries.invalidateQueries(["projects", projectId], {
        exact: true,
        refetchInactive: true,
      }),
      // Dashboard copies use the list cache even after its observer unmounts.
      queries.invalidateQueries(["projects"], {
        exact: true,
        refetchInactive: true,
      }),
    ]);
  const upload = useMutation(
    (item: SupportingUpload) =>
      uploadSupportingDocument(client, projectId, item),
    { onSuccess: refresh },
  );
  const remove = useMutation(
    (name: string) =>
      client.delete(
        `/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(`${SpecialUploadType.supportingDocument}::${name}`)}`,
      ),
    { onSuccess: refresh },
  );
  const update = useMutation(
    ({
      doc,
      metadata,
    }: {
      doc: DocumentData;
      metadata: SupportingDocumentMetadata;
    }) =>
      client.put(
        `/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(`${SpecialUploadType.supportingDocument}::${doc.name}`)}`,
        metadata,
      ),
    { onSuccess: refresh },
  );
  return { upload, remove, update };
};
